<template>
  <div
    v-if="hasEmbeddablePlatform && showEmbed !== false"
    v-show="showEmbed === true"
    :class="[cssClass, { 'social-embed--validating': showEmbed === null }]"
  >
    <div v-if="isFacebook">
      <div class="fb-post" :data-href="url" data-width="500"></div>
    </div>
    <div v-else-if="isYoutube && youtubeEmbedUrl">
      <app-iframe :url="youtubeEmbedUrl" />
    </div>
    <div v-else-if="isTikTok || isTwitter || isInstagram">
      <app-social-blockqoute 
        :platformClass="platformClass"
        :cite="isTikTok ? url: null"
        :dataInstgrmPermalink="isInstagram ? url : null"
        :dataInstgrmVersion="isInstagram ? '14' : null"
        :dataUrl="isTwitter ? url : null"
        :tiktokVideoId="tiktokVideoId"
        :isTikTok="isTikTok"
        :isTwitter="isTwitter"
        :isInstagram="isInstagram"
      />
    </div>
    <div v-else-if="isInstagramMP4">
      <video
        controls
        controlsList="nofullscreen"
        preload="auto"
        class="instagram-video"
        @error="onEmbedFailed"
      >
        <source :src="url" type="video/mp4">
      </video>
    </div>
  </div>
</template>

<script>
import Iframe from '@/components/iframe';
import Blockquote from './blockquote';
import {
  EMBED_CHECK_DELAY_MS,
  buildYoutubeEmbedUrl,
  extractTikTokVideoId,
  isEmbeddablePlatformUrl,
  isFacebookUrl,
  isInstagramMp4Url,
  isInstagramUrl,
  isRenderedEmbedValid,
  isTikTokUrl,
  isTwitterUrl,
  isValidPlatformUrl,
  isYoutubeUrl,
  preflightEmbedUrl
} from './embed-validation';

export default {
  props: {
    url: {
      type: String,
      required: true
    }
  },
  components: {
    'app-iframe': Iframe,
    'app-social-blockqoute': Blockquote
  },
  data() {
    return {
      showEmbed: null,
      embedCheckTimer: null,
      embedObserver: null
    };
  },
  computed: {
    isFacebook() {
      return isFacebookUrl(this.url);
    },
    isYoutube() {
      return isYoutubeUrl(this.url);
    },
    isTikTok() {
      return isTikTokUrl(this.url);
    },
    isTwitter() {
      return isTwitterUrl(this.url);
    },
    isInstagram() {
      return isInstagramUrl(this.url);
    },
    isInstagramMP4() {
      return isInstagramMp4Url(this.url);
    },
    hasEmbeddablePlatform() {
      return isEmbeddablePlatformUrl(this.url);
    },
    needsSdkEmbed() {
      return this.isFacebook || this.isInstagram || this.isTikTok || this.isTwitter;
    },
    cssClass() {
      return this.isYoutube ? 'full-width' : 'half-width';
    },
    platformClass() {
      if (this.isInstagram) {
        return 'instagram-media';
      }
      if (this.isTikTok) {
        return 'tiktok-embed';
      }
      if (this.isTwitter) {
        return 'twitter-tweet';
      }
      return 'blockquote';
    },
    tiktokVideoId() {
      return extractTikTokVideoId(this.url);
    },
    youtubeEmbedUrl() {
      return buildYoutubeEmbedUrl(this.url);
    }
  },
  mounted() {
    this.initializeEmbed();
  },
  destroyed() {
    this.clearEmbedValidation();
    this.cleanup();
  },
  methods: {
    /**
     * Validates URL, runs oEmbed preflight, then loads platform embeds.
     */
    async initializeEmbed() {
      if (!this.hasEmbeddablePlatform) {
        return;
      }

      if (!isValidPlatformUrl(this.url)) {
        this.showEmbed = false;
        return;
      }

      const oembedResult = await preflightEmbedUrl(this.url);
      if (oembedResult === false) {
        this.showEmbed = false;
        return;
      }

      if (this.isYoutube) {
        if (!this.youtubeEmbedUrl) {
          this.showEmbed = false;
          return;
        }
        this.showEmbed = true;
        this.scheduleEmbedValidation();
        return;
      }

      if (this.isInstagramMP4) {
        this.showEmbed = true;
        return;
      }

      if (this.isFacebook) {
        this.loadFacebookSDK(); // https://developers.facebook.com/docs/plugins/embedded-posts/
      }
      if (this.isInstagram) {
        this.loadInstagramSDK();
      }
      if (this.isTikTok) {
        this.loadTikTokSDK(); // https://developers.tiktok.com/doc/embed-videos/
      }
      if (this.isTwitter) {
        this.loadTwitterSDK(); // https://developer.x.com/en/docs/x-for-websites/javascript-api/guides/set-up-twitter-for-websites
      }
    },

    /**
     * Hides the embed when media fails to load or validation fails.
     */
    onEmbedFailed() {
      this.showEmbed = false;
      this.clearEmbedValidation();
    },

    /**
     * Schedules a post-SDK check to detect broken blockquote/iframe embeds.
     */
    scheduleEmbedValidation() {
      this.clearEmbedValidation();
      this.embedCheckTimer = setTimeout(() => {
        this.validateRenderedEmbed();
      }, EMBED_CHECK_DELAY_MS);
    },

    /**
     * Observes DOM changes so validation runs after async SDK rendering.
     */
    observeEmbedRender() {
      if (this.embedObserver || !this.$el) {
        return;
      }

      this.embedObserver = new MutationObserver(() => {
        if (this.$el.querySelector('iframe, .twitter-tweet-rendered, blockquote[data-instgrm-payload-id]')) {
          this.validateRenderedEmbed();
        }
      });

      this.embedObserver.observe(this.$el, {
        childList: true,
        subtree: true
      });
    },

    /**
     * Shows or hides the embed based on rendered platform output.
     */
    validateRenderedEmbed() {
      const valid = isRenderedEmbedValid(this.$el, this.url);
      this.showEmbed = valid;
      if (!valid) {
        this.clearEmbedValidation();
      }
    },

    /**
     * Clears embed validation timers and observers.
     */
    clearEmbedValidation() {
      if (this.embedCheckTimer) {
        clearTimeout(this.embedCheckTimer);
        this.embedCheckTimer = null;
      }
      if (this.embedObserver) {
        this.embedObserver.disconnect();
        this.embedObserver = null;
      }
    },

    /**
     * Runs after platform SDK is ready to process embeds.
     */
    onPlatformSdkReady() {
      this.observeEmbedRender();
      this.scheduleEmbedValidation();
    },

    loadInstagramSDK() {
      if (!window.instgrm && !document.getElementById('instagram-sdk')) {
        const script = document.createElement('script');
        script.id = 'instagram-sdk';
        script.src = 'https://www.instagram.com/embed.js';
        script.async = true;
        script.onload = () => {
          window.instgrm.Embeds.process();
          this.onPlatformSdkReady();
        };
        script.onerror = () => setTimeout(() => this.loadInstagramSDK(), 1000);
        document.body.appendChild(script);
      } else if (window.instgrm) {
        window.instgrm.Embeds.process();
        this.onPlatformSdkReady();
      }
    },
    loadTikTokSDK() {
      if (!window.tiktok && !document.getElementById('tiktok-sdk')) {
        const script = document.createElement('script');
        script.id = 'tiktok-sdk';
        script.src = 'https://www.tiktok.com/embed.js';
        script.async = true;
        script.onload = () => {
          if (window.tiktok) {
            window.tiktok.embed();
          }
          this.onPlatformSdkReady();
        };
        script.onerror = () => {
          setTimeout(() => this.loadTikTokSDK(), 1000);
        };
        document.body.appendChild(script);
      } else if (window.tiktok) {
        window.tiktok.embed();
        this.onPlatformSdkReady();
      }
    },
    loadTwitterSDK() {
      if (!window.twttr && !document.getElementById('twitter-sdk')) {
        const script = document.createElement('script');
        script.id = 'twitter-sdk';
        script.src = 'https://platform.twitter.com/widgets.js';
        script.async = true;
        script.onload = () => {
          window.twttr.widgets.load(this.$el);
          this.onPlatformSdkReady();
        };
        script.onerror = () => setTimeout(() => this.loadTwitterSDK(), 1000);
        document.body.appendChild(script);
      } else if (window.twttr) {
        window.twttr.widgets.load(this.$el);
        this.onPlatformSdkReady();
      }
    },
    loadFacebookSDK() {
      if (window.FB) {
        this.renderFacebookPost();
        this.onPlatformSdkReady();
      } else if (!document.getElementById('facebook-sdk')) {
        const script = document.createElement('script');
        script.id = 'facebook-sdk';
        script.src = 'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v20.0';
        script.async = true;
        script.onload = () => {
          this.renderFacebookPost();
          this.onPlatformSdkReady();
        };
        script.onerror = () => setTimeout(() => this.loadFacebookSDK(), 1000);
        document.body.appendChild(script);
      } else {
        this.renderFacebookPost();
        this.onPlatformSdkReady();
      }
    },
    renderFacebookPost() {
      this.$nextTick(() => {
        if (window.FB && window.FB.XFBML) {
          window.FB.XFBML.parse(this.$el);
        }
      });
    },
    cleanup() {
      const elements = document.querySelectorAll('#tiktok-sdk, #ttEmbedLibScript, #facebook-sdk, #instagram-sdk, #twitter-sdk, iframe[title="Twitter settings iframe"], iframe[title="Twitter analytics iframe"]');
      if (elements && elements.length) {
        elements.forEach((el) => el.remove());
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.full-width {
  width: 100%;
}
.half-width {
  width: 50%;
  margin: 0 auto;
  .instagram-video {
    width: 100%;
    min-height: 40rem;
  }
}
.social-embed--validating {
  position: absolute;
  left: -9999px;
  visibility: hidden;
  pointer-events: none;
}
</style>
